import React from 'react';
import PropTypes from 'prop-types';

// Fonction utilitaire pour obtenir la couleur en fonction du statut
const getStatusColor = (status) => {
  const statusLower = status.toLowerCase();
  if (statusLower.includes('livré') || statusLower.includes('delivered')) {
    return 'bg-green-500';
  } else if (statusLower.includes('transit')) {
    return 'bg-blue-500';
  } else if (statusLower.includes('pickup') || statusLower.includes('ready')) {
    return 'bg-purple-500';
  } else if (statusLower.includes('exception') || statusLower.includes('erreur')) {
    return 'bg-red-500';
  } else {
    return 'bg-gray-500';
  }
};

// Fonction pour formater la date en français
const formatDate = (dateString) => {
  const date = new Date(dateString);
  const options = { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  };
  return date.toLocaleDateString('fr-FR', options);
};

const TrackingResult = ({ trackingInfo }) => {
  if (!trackingInfo || !trackingInfo.output || !trackingInfo.output.completeTrackResults) {
    return null;
  }

  const { completeTrackResults } = trackingInfo.output;

  return (
    <div className="mt-8">
      
      {completeTrackResults.map((result, index) => {
        // Récupérer le premier trackResult s'il existe
        const trackResult = result.trackResults && result.trackResults.length > 0 ? result.trackResults[0] : null;
        const statusText = trackResult?.latestStatusDetail?.statusByLocale || 'Statut inconnu';
        const statusColor = getStatusColor(statusText);
        
        return (
          <div key={index} className="mb-10 bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
            {/* En-tête avec numéro de suivi et statut */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white p-6">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-2xl font-bold tracking-tight">{result.trackingNumber}</h3>
                  <p className="text-blue-100 mt-1">
                    {trackResult?.serviceDetail?.description || 'Service FedEx'}
                  </p>
                </div>
                <div className="flex items-center">
                  <div className={`${statusColor} text-white px-4 py-2 rounded-full font-medium`}>
                    {statusText}
                  </div>
                </div>
              </div>
            </div>

            {/* Contenu principal */}
            {trackResult ? (
              <div className="p-6">
                {/* Informations d'expédition */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  {/* Expéditeur */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-700 mb-2">Expéditeur</h4>
                    {trackResult.shipperInformation?.address && (
                      <div>
                        <p>{trackResult.shipperInformation.address.city || ''}</p>
                        <p>{trackResult.shipperInformation.address.stateOrProvinceCode || ''} {trackResult.shipperInformation.address.countryCode || ''}</p>
                      </div>
                    )}
                  </div>
                  
                  {/* Destinataire */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-700 mb-2">Destinataire</h4>
                    {trackResult.recipientInformation?.address && (
                      <div>
                        <p>{trackResult.recipientInformation.address.city || ''}</p>
                        <p>{trackResult.recipientInformation.address.stateOrProvinceCode || ''} {trackResult.recipientInformation.address.countryCode || ''}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Détails du colis */}
                {trackResult.packageDetails && (
                  <div className="mb-8 bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-700 mb-2">Détails du colis</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <p className="text-sm text-gray-500">Type</p>
                        <p>{trackResult.packageDetails.packagingDescription?.description || 'Package'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Poids</p>
                        <p>{trackResult.packageDetails.weightAndDimensions?.weight?.[0]?.value || ''} {trackResult.packageDetails.weightAndDimensions?.weight?.[0]?.unit || ''}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Dimensions</p>
                        <p>
                          {trackResult.packageDetails.weightAndDimensions?.dimensions?.[0] ? 
                            `${trackResult.packageDetails.weightAndDimensions.dimensions[0].length}x${trackResult.packageDetails.weightAndDimensions.dimensions[0].width}x${trackResult.packageDetails.weightAndDimensions.dimensions[0].height} ${trackResult.packageDetails.weightAndDimensions.dimensions[0].units}` : 
                            'Non disponible'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Historique des scans avec timeline moderne */}
                <div className="mt-8">
                  <h4 className="text-lg font-semibold mb-4 text-gray-800 border-b pb-2">Historique de suivi</h4>
                  
                  <div className="relative">
                    {/* Ligne verticale de la timeline */}
                    <div className="absolute left-0 top-0 h-full w-0.5 bg-blue-200 ml-2.5"></div>
                    
                    {/* Événements */}
                    <div className="space-y-6 ml-6">
                      {trackResult.scanEvents && trackResult.scanEvents.map((event, eventIndex) => {
                        const isLatest = eventIndex === 0;
                        return (
                          <div key={eventIndex} className={`relative ${isLatest ? 'pb-8' : ''}`}>
                            {/* Point sur la timeline */}
                            <div className={`absolute -left-6 mt-1.5 w-5 h-5 rounded-full border-4 ${isLatest ? 'border-blue-500 bg-white' : 'border-blue-200 bg-white'}`}></div>
                            
                            {/* Contenu de l'événement */}
                            <div className={`p-4 rounded-lg ${isLatest ? 'bg-blue-50 border border-blue-100' : 'bg-white border border-gray-100'}`}>
                              <p className={`font-semibold ${isLatest ? 'text-blue-700' : 'text-gray-700'}`}>{event.derivedStatus}</p>
                              <p className="text-sm text-gray-600 mt-1">{formatDate(event.date)}</p>
                              
                              {event.scanLocation && (
                                <div className="mt-2 flex items-center text-sm text-gray-500">
                                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                                  </svg>
                                  <span>
                                    {[event.scanLocation.city, event.scanLocation.stateOrProvinceCode, event.scanLocation.countryCode]
                                      .filter(Boolean)
                                      .join(', ')}
                                  </span>
                                </div>
                              )}
                              
                              {event.exceptionDescription && (
                                <div className="mt-2 text-sm text-amber-600 bg-amber-50 p-2 rounded">
                                  {event.exceptionDescription}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-6">
                <div className="bg-red-50 text-red-600 p-4 rounded-lg flex items-center">
                  <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  <span>Aucune information de suivi trouvée pour ce numéro.</span>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};


TrackingResult.propTypes = {
  trackingInfo: PropTypes.shape({
    output: PropTypes.shape({
      completeTrackResults: PropTypes.arrayOf(
        PropTypes.shape({
          trackingNumber: PropTypes.string,
          trackResults: PropTypes.arrayOf(
            PropTypes.shape({
              latestStatusDetail: PropTypes.shape({
                statusByLocale: PropTypes.string
              }),
              scanEvents: PropTypes.arrayOf(
                PropTypes.shape({
                  derivedStatus: PropTypes.string,
                  date: PropTypes.string,
                  scanLocation: PropTypes.shape({
                    city: PropTypes.string,
                    stateOrProvinceCode: PropTypes.string,
                    countryCode: PropTypes.string
                  })
                })
              )
            })
          )
        })
      )
    })
  })
};

export default TrackingResult;
